import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Reveal from '../ui/Reveal';
import { FiLayers, FiUsers, FiCompass, FiShield } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const issues = [
  {
    id: 'system',
    title: 'Missing System',
    icon: FiLayers,
    desc: 'The process lives entirely in your head. When you stop communicating, execution stops. We extract the logic and build a repeatable architecture.'
  },
  {
    id: 'adoption',
    title: 'Unadopted System',
    icon: FiUsers,
    desc: 'You bought the software and wrote the handbook, but the team still defaults to asking you. We fix the friction points that prevent adoption.'
  },
  {
    id: 'decision',
    title: 'Missing Decision Right',
    icon: FiCompass,
    desc: 'Your team knows what to do, but they wait for your permission to do it. We build clear decision maps so work moves without your signature.'
  },
  {
    id: 'boundary',
    title: 'Missing Boundary',
    icon: FiShield,
    desc: 'Escalations bypass the structure and land directly on your desk. We build protection layers that resolve issues before they reach you.'
  }
];

export default function WhatWeDo() {
  const [activeId, setActiveId] = useState(issues[0].id);

  return (
    <section className="py-24 md:py-32 bg-brand-olive text-brand-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="max-w-3xl mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-6">This is not coaching. This is an operating system for your business.</h2>
            <p className="text-lg text-brand-cream/80 font-light leading-relaxed">
              Clarity90 finds the root constraint and builds the structure required for execution without founder dependency. We target four specific structural failures.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 space-y-4">
            {issues.map((issue) => (
              <button
                key={issue.id}
                onClick={() => setActiveId(issue.id)}
                className={`w-full text-left p-6 transition-all duration-300 border-l-4 ${
                  activeId === issue.id
                    ? 'bg-brand-deep border-brand-terra text-brand-cream'
                    : 'bg-transparent border-transparent text-brand-cream/60 hover:text-brand-cream hover:bg-brand-deep/30'
                }`}
              >
                <div className="flex items-center gap-4">
                  <SafeIcon icon={issue.icon} size={24} className={activeId === issue.id ? 'text-brand-terra' : 'text-current'} />
                  <h3 className="font-serif text-xl">{issue.title}</h3>
                </div>
              </button>
            ))}
          </div>

          <div className="md:col-span-7 bg-brand-deep p-8 md:p-12 min-h-[300px] flex items-center border border-brand-cream/10">
            <AnimatePresence mode="wait">
              {issues.map((issue) => activeId === issue.id && (
                <motion.div
                  key={issue.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <SafeIcon icon={issue.icon} size={40} className="text-brand-terra mb-6" />
                  <h4 className="font-serif text-3xl mb-4">{issue.title}</h4>
                  <p className="text-xl text-brand-cream/80 font-light leading-relaxed">
                    {issue.desc}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}